/**
 * Authentication Context
 *
 * Manages authentication state for the studio application.
 * Uses JWT token-based auth with auth-api multi-tenant service.
 *
 * @module @humanizer/studio/contexts/AuthContext
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { AUTH_API_URL, TENANT_ID } from '../config';
import { createLogger } from '../utils/debug';

const log = createLogger('Auth');

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export type UserRole = 'free' | 'member' | 'pro' | 'premium' | 'admin';

export type AuthMethod = 'password' | 'oauth' | 'mixed';

export interface User {
  id: string;
  email: string;
  role: UserRole;
  tenantId: string;
  authMethod?: AuthMethod;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterCredentials {
  email: string;
  password: string;
  ageConfirmed: boolean;
  termsAccepted: boolean;
  privacyAccepted: boolean;
}

export interface AuthContextValue extends AuthState {
  login: (credentials: LoginCredentials) => Promise<boolean>;
  register: (credentials: RegisterCredentials) => Promise<boolean>;
  logout: () => void;
  clearError: () => void;
  getAuthHeaders: () => Record<string, string>;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

// Storage key for JWT token
const TOKEN_STORAGE_KEY = 'humanizer_auth_token';

// ═══════════════════════════════════════════════════════════════════════════
// TOKEN HELPERS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get stored auth token
 */
function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Store auth token
 */
function storeToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    log.warn('Failed to store auth token');
  }
}

/**
 * Clear stored auth token
 */
function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Build auth headers for API requests
 */
function buildAuthHeaders(token: string | null): Record<string, string> {
  const headers: Record<string, string> = {
    'X-Tenant-ID': TENANT_ID,
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONTEXT
// ═══════════════════════════════════════════════════════════════════════════

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}

// ═══════════════════════════════════════════════════════════════════════════
// PROVIDER
// ═══════════════════════════════════════════════════════════════════════════

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });
  const [token, setToken] = useState<string | null>(null);

  // ─────────────────────────────────────────────────────────────────────────
  // INITIALIZE ON MOUNT — check URL params for OAuth token or load from storage
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    async function init() {
      // ELECTRON: Load real profile from local auth system
      const isElectron = typeof window !== 'undefined' && !!window.isElectron;
      if (isElectron) {
        try {
          const profile = await window.electronLocalAuth?.getActiveProfile();
          if (profile) {
            // Map local profile to User shape (backwards-compatible)
            setState({
              user: {
                id: profile.id,
                email: profile.cloudLink?.email || `${profile.osUsername}@local`,
                role: profile.localRole === 'owner' ? 'admin' : profile.localRole === 'member' ? 'member' : 'free',
                tenantId: 'local',
                authMethod: profile.cloudLink ? 'oauth' : 'password',
              },
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
          } else {
            // Fallback: no profile found (shouldn't happen — migration creates one)
            setState({
              user: {
                id: 'local-user',
                email: 'local@localhost',
                role: 'admin',
                tenantId: 'local',
                authMethod: 'password',
              },
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
          }
        } catch {
          // Fallback if IPC fails
          setState({
            user: {
              id: 'local-user',
              email: 'local@localhost',
              role: 'admin',
              tenantId: 'local',
              authMethod: 'password',
            },
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        }
        return;
      }

      // Check for OAuth callback with token in URL
      const queryParams = new URLSearchParams(window.location.search);
      const urlToken = queryParams.get('token');
      const oauthError = queryParams.get('error');
      const isNewUser = queryParams.get('isNewUser') === 'true';

      // Clear URL params after reading
      if (urlToken || oauthError) {
        window.history.replaceState({}, '', window.location.pathname);
      }

      if (oauthError) {
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: `OAuth login failed: ${oauthError}`,
        });
        return;
      }

      // Use token from URL (OAuth) or storage (returning user)
      const authToken = urlToken || getStoredToken();

      if (!authToken) {
        setState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      // Store token if from OAuth
      if (urlToken) {
        storeToken(urlToken);
        setToken(urlToken);
      } else {
        setToken(authToken);
      }

      // Verify token and get user info
      try {
        const response = await fetch(`${AUTH_API_URL}/auth/me`, {
          headers: buildAuthHeaders(authToken),
        });

        if (!response.ok) {
          // Token invalid or expired
          clearToken();
          setToken(null);
          setState(prev => ({ ...prev, isLoading: false }));
          return;
        }

        const data = await response.json();
        const user: User = {
          id: data.id,
          email: data.email,
          role: data.role,
          tenantId: data.tenantId ?? TENANT_ID,
          authMethod: data.authMethod,
        };

        setState({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });

        // New user welcome handled by UI notification system
      } catch {
        clearToken();
        setToken(null);
        setState(prev => ({ ...prev, isLoading: false }));
      }
    }

    init();
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // AUTH ACTIONS
  // ─────────────────────────────────────────────────────────────────────────

  const login = useCallback(async (credentials: LoginCredentials): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await fetch(`${AUTH_API_URL}/auth/login`, {
        method: 'POST',
        headers: buildAuthHeaders(null),
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const error = await response.json();
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: error.message ?? error.error ?? 'Login failed',
        }));
        return false;
      }

      const data = await response.json();

      // Store token
      storeToken(data.token);
      setToken(data.token);

      const user: User = {
        id: data.user.id,
        email: data.user.email,
        role: data.user.role,
        tenantId: data.user.tenantId ?? TENANT_ID,
        authMethod: 'password',
      };

      setState({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      return true;
    } catch (err) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Network error',
      }));
      return false;
    }
  }, []);

  const register = useCallback(async (credentials: RegisterCredentials): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await fetch(`${AUTH_API_URL}/auth/register`, {
        method: 'POST',
        headers: buildAuthHeaders(null),
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: error.message ?? error.error ?? 'Registration failed',
        }));
        return false;
      }

      const data = await response.json();

      // Store token (user is logged in after registration)
      storeToken(data.token);
      setToken(data.token);

      const user: User = {
        id: data.user.id,
        email: data.user.email,
        role: data.user.role,
        tenantId: data.user.tenantId ?? TENANT_ID,
        authMethod: 'password',
      };

      setState({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      return true;
    } catch (err) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Network error',
      }));
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setToken(null);
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const getAuthHeaders = useCallback((): Record<string, string> => {
    // Always read from localStorage directly to avoid race conditions
    // where React state hasn't updated yet but API calls are being made
    const storedToken = getStoredToken();
    return buildAuthHeaders(storedToken || token);
  }, [token]);

  // ─────────────────────────────────────────────────────────────────────────
  // CONTEXT VALUE
  // ─────────────────────────────────────────────────────────────────────────

  const value = useMemo<AuthContextValue>(() => ({
    ...state,
    login,
    register,
    logout,
    clearError,
    getAuthHeaders,
  }), [state, login, register, logout, clearError, getAuthHeaders]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// UTILITY HOOKS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Hook to check if user has at least the specified tier.
 */
export function useRequiresTier(minTier: UserRole): boolean {
  const { user } = useAuth();

  const tierOrder: Record<UserRole, number> = {
    free: 0,
    member: 1,
    pro: 2,
    premium: 3,
    admin: 4,
  };

  if (!user) return false;
  return tierOrder[user.role] >= tierOrder[minTier];
}

/**
 * Hook to check if user is admin.
 */
export function useIsAdmin(): boolean {
  const { user } = useAuth();
  return user?.role === 'admin';
}
